import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { EASE } from "./Reveal";

const RevealLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ scrollTo }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden grain" data-testid="hero-section">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src="/assets/hero.jpg"
          alt="Custom speaker wall build by Mobile One Audio"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-[#0A0B0E]/55 to-[#0A0B0E]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0B0E]/80 via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8 pb-10 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
          className="font-tech text-xs sm:text-sm uppercase tracking-[0.35em] text-[#FF5500] mb-6"
        >
          New Castle, Delaware — Custom Car Audio & Auto Styling
        </motion.p>

        <h1 className="font-display font-extrabold tracking-tight leading-[0.95] text-[13vw] sm:text-7xl lg:text-8xl">
          <RevealLine delay={0.55}>TURN HEADS.</RevealLine>
          <RevealLine delay={0.7} className="text-outline">
            SHAKE PAVEMENT.
          </RevealLine>
          <RevealLine delay={0.85} className="text-[#FF5500]">
            BUILD DIFFERENT.
          </RevealLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
          className="mt-7 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed"
        >
          From booming sound systems to full street-ready makeovers — premium car audio,
          custom installs, and head-turning upgrades for cars, trucks, and everything in
          between. Clean craftsmanship on every build.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.25, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-quote-button"
            onClick={() => scrollTo("#quote")}
            className="group flex items-center gap-3 rounded-full bg-[#FF5500] px-8 py-4 font-tech text-sm font-bold uppercase tracking-widest text-white transition-all duration-300 hover:scale-[1.04] volt-glow"
          >
            Get a Quote
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </button>
          <button
            data-testid="hero-work-button"
            onClick={() => scrollTo("#gallery")}
            className="rounded-full border border-white/25 px-8 py-4 font-tech text-sm font-bold uppercase tracking-widest text-white/90 transition-colors duration-300 hover:border-[#FF5500] hover:text-[#FF5500]"
          >
            View Our Work
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-white/10 pt-6"
        >
          {[
            ["Location", "867 S Dupont Hwy, Unit 2 — New Castle, DE"],
            ["Builds", "Audio / Wraps / Tint / LED / Rims / Home A/V"],
            ["Call The Shop", "(302) 268-6466"],
          ].map(([k, v]) => (
            <div key={k}>
              <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5500] mb-1.5">{k}</p>
              <p className="text-sm text-white/60">{v}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollTo("#audio")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 right-6 sm:right-10 z-10 hidden sm:flex h-12 w-12 items-center justify-center rounded-full border border-white/20 text-white/60 hover:text-[#FF5500] hover:border-[#FF5500] transition-colors"
        aria-label="Scroll down"
      >
        <motion.span animate={{ y: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={18} />
        </motion.span>
      </motion.button>
    </section>
  );
}
